import { apiClient } from '@/lib/apiClient';

export type EmailProvider = 'imap' | 'hostinger';

export interface EmailAccount {
  id?: number;
  email_address: string;
  display_name: string;
  signature_html?: string;
  /** Mail provider — 'imap' (default) or 'hostinger' (Hostinger Mail API). */
  provider?: EmailProvider;
  /** Resolved server-side for Hostinger accounts; null otherwise. */
  hostinger_resource_id?: string | null;
  // IMAP/SMTP fields — null for Hostinger accounts
  imap_server?: string | null;
  imap_port?: number | null;
  imap_username?: string | null;
  imap_password?: string;
  imap_mailbox?: string | null;
  smtp_server?: string | null;
  smtp_port?: number | null;
  smtp_username?: string | null;
  smtp_password?: string;
  use_ssl?: boolean;
  trust_self_signed?: boolean;
  max_sync_per_run?: number;
  sync_window_days?: number;
  sync_delete_email_from_server?: boolean;
}

/**
 * Payload for creating/updating an account.
 * NOTE: `hostinger_token` is write-only — the backend never returns it.
 */
export interface EmailAccountInput {
  provider: EmailProvider;
  email_address: string;
  display_name: string;
  signature_html?: string;
  hostinger_token?: string;
  imap_server?: string;
  imap_port?: number;
  imap_username?: string;
  imap_password?: string;
  imap_mailbox?: string;
  smtp_server?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  use_ssl?: boolean;
  trust_self_signed?: boolean;
  max_sync_per_run?: number;
  sync_window_days?: number;
  sync_delete_email_from_server?: boolean;
}

export interface EmailAccountResponse {
  success: boolean;
  account?: EmailAccount;
  account_id?: number;
  hostinger_resource_id?: string | null;
  items?: EmailAccount[];
  message?: string;
  user_id?: number;
  limit?: number;
  offset?: number;
}

export interface HostingerWebhookResponse {
  success: boolean;
  webhook_id?: string;
  webhook_url?: string;
  has_secret?: boolean;
  message?: string;
}

/**
 * Fetch all email accounts for the current user
 */
export const fetchEmailAccounts = async (): Promise<EmailAccount[]> => {
  try {
    const response = await apiClient.get<EmailAccountResponse | EmailAccount[]>('/email/accounts');
    return Array.isArray(response) ? response : response.items || [];
  } catch (error: any) {
    console.error('Failed to fetch email accounts:', error);
    throw new Error(error?.message || 'Failed to fetch email accounts');
  }
};

/**
 * Fetch a single email account by ID
 */
export const fetchEmailAccount = async (accountId: number): Promise<EmailAccount> => {
  try {
    const response = await apiClient.get<EmailAccountResponse | EmailAccount>(`/email/accounts/${accountId}`);
    const account = 'email_address' in response ? response : response.account;
    if (!account) {
      throw new Error('Account not found');
    }
    return account;
  } catch (error: any) {
    console.error(`Failed to fetch email account ${accountId}:`, error);
    throw new Error(error?.message || 'Failed to fetch email account');
  }
};

/**
 * Create a new email account (IMAP or Hostinger)
 */
export const createEmailAccount = async (accountData: EmailAccountInput): Promise<number> => {
  try {
    const response = await apiClient.post<EmailAccountResponse>('/email/accounts', accountData);
    const accountId = response.account_id || response.account?.id;
    if (!accountId) {
      throw new Error('Failed to create account - no ID returned');
    }
    return accountId;
  } catch (error: any) {
    console.error('Failed to create email account:', error);
    throw new Error(error?.message || 'Failed to create email account');
  }
};

/**
 * Update an existing email account
 */
export const updateEmailAccount = async (
  accountId: number,
  accountData: Partial<EmailAccountInput>
): Promise<void> => {
  try {
    await apiClient.put<EmailAccountResponse>(`/email/accounts/${accountId}`, accountData);
  } catch (error: any) {
    console.error(`Failed to update email account ${accountId}:`, error);
    throw new Error(error?.message || 'Failed to update email account');
  }
};

/**
 * Delete an email account
 */
export const deleteEmailAccount = async (accountId: number): Promise<void> => {
  try {
    await apiClient.delete<EmailAccountResponse>(`/email/accounts/${accountId}`);
  } catch (error: any) {
    console.error(`Failed to delete email account ${accountId}:`, error);
    throw new Error(error?.message || 'Failed to delete email account');
  }
};

/**
 * Enable live inbox push for a Hostinger account — registers the inbound webhook.
 * Returns the webhook URL + id. Hostinger accounts only.
 */
export const setupHostingerWebhook = async (accountId: number): Promise<HostingerWebhookResponse> => {
  try {
    return await apiClient.post<HostingerWebhookResponse>(`/email/accounts/${accountId}/hostinger-webhook`);
  } catch (error: any) {
    console.error(`Failed to set up Hostinger webhook for account ${accountId}:`, error);
    throw new Error(error?.message || 'Failed to enable live inbox');
  }
};

/**
 * Disable live inbox push for a Hostinger account — tears down the webhook.
 */
export const deleteHostingerWebhook = async (accountId: number): Promise<HostingerWebhookResponse> => {
  try {
    return await apiClient.delete<HostingerWebhookResponse>(`/email/accounts/${accountId}/hostinger-webhook`);
  } catch (error: any) {
    console.error(`Failed to delete Hostinger webhook for account ${accountId}:`, error);
    throw new Error(error?.message || 'Failed to disable live inbox');
  }
};
